import React from 'react';
import { FALLBACK_COLOR, STATUS_COLORS } from './timeline-constants';

export type TimelineItem = {
  id: string;
  state: string;
  title: string;
  desc: string;
  date: string | null;
  time: string | null;
  note?: string | null;
  actor?: string | null;
  icon: React.ReactNode;
  isCurrent: boolean;
};

interface TimelineEntryProps {
  item: TimelineItem;
  isLast: boolean;
}

export default function TimelineEntry({ item, isLast }: TimelineEntryProps) {
  const colors = STATUS_COLORS[item.state] ?? FALLBACK_COLOR;

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {/* connecting rail */}
      {!isLast && (
        <span className="absolute left-[7px] top-4 bottom-0 w-px bg-slate-200" aria-hidden />
      )}

      {/* dot */}
      <span
        className={[
          'relative z-10 mt-1 h-3.5 w-3.5 flex-shrink-0 rounded-full ring-4 ring-white',
          colors.dot,
        ].join(' ')}
        aria-hidden
      />

      {/* content */}
      <div className="flex-1 min-w-0 -mt-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={[
              'inline-flex h-6 w-6 items-center justify-center rounded-full flex-shrink-0',
              colors.badgeBg,
              colors.badgeText,
            ].join(' ')}
          >
            {item.icon}
          </span>

          <span className="text-sm font-semibold text-slate-800">{item.title}</span>

          {item.isCurrent && (
            <span className="inline-flex h-5 items-center rounded-full bg-blue-50 px-2 text-[10px] font-semibold text-blue-600">
              Current
            </span>
          )}
        </div>

        <p className="mt-1 text-xs text-slate-500 pl-8 break-words">{item.desc}</p>

        <div className="mt-1 pl-8 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
          {(item.date || item.time) && (
            <span>
              {item.date}
              {item.time ? ` · ${item.time}` : ''}
            </span>
          )}
          {item.actor && (
            <span className="text-slate-400">
              by <span className="font-medium text-slate-500">{item.actor}</span>
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
